import axios from 'axios';
import GA4 from './lib/ga4';

/**
 * Assign and apply the default properties including overrides as well as
 * forwarding Google Analytics 4 Measurement Protocol Session to Segment.
 *
 * @param {Object} overrides
 * @param {Object} defaults
 * @return {Object}
 */
function assignProperties(overrides = {}, defaults = {}) {
    return {
        ...defaults,
        ...overrides,
        ...GA4.properties(),
    };
}

/**
 * Assign and apply the default options including overrides as well as
 * forwarding Google Analytics 4 Measurement Protocol Session to Segment.
 *
 * @param {Object} overrides
 * @param {Object} defaults
 * @return {Object}
 */
function assignOptions(overrides = {}, defaults = {}) {
    const gaOptions = GA4.options();
    const options = {
        ...defaults,
        ...overrides,
    };

    if ('integrations' in gaOptions) {
        return {
            ...options,
            integrations: {
                ...options.integrations,
                ...gaOptions.integrations,
            },
        };
    }

    return options;
}

const Tracking = {
    /**
     * Default properties to send with every event.
     */
    defaultProperties: {},

    /**
     * Default options to send with every event.
     */
    defaultOptions: {},

    /**
     * Initialize the Segment tracking script a.k.a the Segment Snippet.
     *
     * @param {String} writeKey
     * @return {void}
     * @version v4.15.2
     */
    init: writeKey => {
        /* eslint-disable */
        // prettier-ignore
        !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey=writeKey;analytics.SNIPPET_VERSION="4.15.2";
        analytics.load(writeKey);
        }}();
        /* eslint-enable */
    },

    /**
     * Configure the default properties to use for all tracking calls.
     *
     * @param {Object} properties
     * @return {void}
     */
    setDefaultProperties: properties => {
        Tracking.defaultProperties = properties;
    },

    /**
     * Configure the default options to use for all tracking calls.
     *
     * @param {Object} options
     * @return {void}
     */
    setDefaultOptions: options => {
        Tracking.defaultOptions = options;
    },

    /**
     * Track a page view in Segment.
     * @see https://segment.com/docs/libraries/analytics.js/#page
     *
     * @param  {String} url
     * @return {Promise}
     */
    trackPage: (url = null) => {
        const href = url || window.location.href;

        return new Promise((resolve, reject) => {
            if (typeof window.analytics !== 'undefined') {
                const parser = document.createElement('a');
                parser.href = href;

                window.analytics.page(
                    assignProperties(
                        {
                            url: href,
                            path: parser.pathname,
                            search: parser.search,
                        },
                        Tracking.defaultProperties
                    ),
                    assignOptions({}, Tracking.defaultOptions),
                    resolve
                );
            } else {
                reject('Analytics not loaded');
            }
        });
    },

    /**
     * Track an event in Segment.
     * @see https://segment.com/docs/libraries/analytics.js/#track
     *
     * @param  {String} event
     * @param  {Object} properties
     * @param  {Object} options
     * @return {Promise}
     */
    track: (event, properties = {}, options = {}) =>
        new Promise((resolve, reject) => {
            if (typeof window.analytics !== 'undefined') {
                window.analytics.track(
                    event,
                    assignProperties(properties, Tracking.defaultProperties),
                    assignOptions(options, Tracking.defaultOptions),
                    resolve
                );
            } else {
                reject('Analytics not loaded');
            }
        }),

    /**
     * Alias/identify a user and track a form submit in Segment.
     * @see https://segment.com/docs/libraries/analytics.js/#track-form
     *
     * @param  {String} event
     * @param  {Array} properties
     * @param  {Array} identifyProperties
     * @param  {Object} options
     * @return {Promise}
     */
    trackForm: (event, properties = [], identifyProperties = [], options = {}) => {
        const formFields = {};

        properties.forEach(property => {
            const element = document.querySelector(`input[name="${property}"]`);

            if (element) {
                formFields[property] = element.value;
            }
        });

        if (!('email' in formFields)) {
            return Tracking.track(
                event,
                assignProperties(formFields, Tracking.defaultProperties),
                assignOptions(options, Tracking.defaultOptions)
            );
        }

        let identityFields = {};

        if (identifyProperties) {
            identifyProperties.forEach(property => {
                const el = document.querySelector(`input[name="${property}"]`);

                if (el) {
                    identityFields[property] = el.value;
                }
            });
        } else {
            identityFields = formFields;
        }

        return Tracking.alias(formFields.email)
            .then(() =>
                Tracking.identify(
                    formFields.email,
                    assignProperties(identityFields, Tracking.defaultProperties)
                )
            )
            .then(() =>
                Tracking.track(
                    event,
                    assignProperties(formFields, Tracking.defaultProperties),
                    assignOptions(options, Tracking.defaultOptions)
                )
            );
    },

    /**
     * Identify a user.
     * @see https://segment.com/docs/libraries/analytics.js/#identify
     *
     * @param  {String} userId
     * @param  {Object} traits
     * @return {Promise}
     */
    identify: (userId, traits = {}) =>
        new Promise((resolve, reject) => {
            if (typeof window.analytics !== 'undefined') {
                window.analytics.identify(
                    userId,
                    assignProperties(traits, Tracking.defaultProperties),
                    resolve
                );
            } else {
                reject('Analytics not loaded');
            }
        }),

    /**
     * Alias a user if the user does not exist in mixpanel.
     * @see https://segment.com/docs/libraries/analytics.js/#alias
     *
     * @param  {String} userId
     * @return {Promise}
     */
    alias: userId =>
        new Promise((resolve, reject) => {
            if (typeof window.analytics !== 'undefined') {
                axios
                    .get('https://pod-point.com/actions/segment/user/fetch', {
                        params: {
                            id: userId,
                            responseType: 'json',
                        },
                    })
                    .then(res => {
                        if (res && res.data) {
                            resolve('User already aliased');
                        } else {
                            window.analytics.alias(userId, null, null, resolve);
                        }
                    })
                    .catch(err => {
                        reject(err);
                    });
            } else {
                reject('Analytics not loaded');
            }
        }),

    /**
     * Clears the cookies/localStorage set by Segment, resetting the id and traits of the user.
     * @see https://segment.com/docs/sources/website/analytics.js/#reset-logout
     *
     * @return {Promise}
     */
    reset: () =>
        new Promise((resolve, reject) => {
            if (typeof window.analytics !== 'undefined') {
                window.analytics.reset();
                resolve();
            } else {
                reject('Analytics not loaded');
            }
        }),

    /**
     * Identify any user we have stored in local storage.
     *
     * @return {Promise}
     */
    identifyFromLocalStorage: () =>
        new Promise((resolve, reject) => {
            if (
                typeof window.localStorage !== 'undefined' &&
                window.localStorage.ajs_user_traits &&
                window.localStorage.ajs_user_traits !== '{}'
            ) {
                const localStorageUser = JSON.parse(window.localStorage.ajs_user_traits);

                Tracking.identify(
                    localStorageUser.email,
                    assignProperties(localStorageUser, Tracking.defaultProperties)
                ).then(resolve);
            } else {
                reject('Local storage not supported or not found');
            }
        }),

    /**
     * Send all the page details to Segment.
     * @see https://segment.com/docs/libraries/analytics.js/#page
     *
     * @param  {Object|null} details
     * @return {Promise}
     */
    page: details =>
        new Promise((resolve, reject) => {
            if (typeof window.analytics !== 'undefined') {
                window.analytics.page(
                    assignProperties(details, Tracking.defaultProperties),
                    assignOptions({}, Tracking.defaultOptions),
                    resolve
                );
            } else {
                reject('Analytics not loaded');
            }
        }),
};

export default Tracking;



// WEBPACK FOOTER //
// ./src/index.js